import React from "react";
import { Outlet, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "store/src/slices/auth-slice";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarMenu,
  SidebarProvider,
  SidebarRail,
  SidebarHeader,
} from "ui/components/sidebar";
import { NavItem } from "@/components/nav/item";
import { NavItemWithBadge } from "@/components/nav/nav-item-with-badge";
import {
  FILETEXT,
  INBOX,
  USER,
  MESSAGE,
  USERS,
  PENDING,
  AWARD,
  PENDING_REVIEW,
  PENDING_SELF_REVIEW,
  REVIEWER_LOAD,
  REVIEW_COVERAGE,
  PIECHART,
} from "icons";
import Loader from "@/components/loader";
import { QuadProvider, useQuad } from "./context";
import { useFeedbackVisibility } from "./hooks/use-feedback-visibility";

import { StatusDisplay } from "@/components/status/display";
import { CollapsiblePerformanceHeader } from "./collapsible-header";

const PerformanceLayout = () => {
  const { quadId } = useParams();
  const { hasGivenFeedbacks, isLoading } = useFeedbackVisibility(quadId);

  if (isLoading) {
    return <Loader />;
  }
  return (
    <QuadProvider quadId={quadId}>
      <div className="flex flex-1">
        <SidebarProvider
          className="w-fit overflow-hidden max-h-dvh"
          name="performance"
        >
          <Sidebar
            collapsible="icon"
            className="sidebar-height border-r left-auto inset-y-auto "
          >
            <SidebarHeader className="px-3 py-2 border-b">
              <CollapsiblePerformanceHeader />
            </SidebarHeader>
            <SidebarContent>
              <SidebarGroup>
                <SidebarMenu>
                  <NavItemWithBadge
                    title="Feedbacks"
                    icon={INBOX}
                    url={`/performance/review/${quadId}/feedbacks`}
                    collection="performance_review"
                    filters={{
                      quadrimester: { _eq: quadId },
                      reviewee: { user: { _eq: "$CURRENT_USER" } },
                      reviewer: { user: { _neq: "$CURRENT_USER" } },
                      status: { _eq: "pending" },
                    }}
                  />
                  <NavItem
                    title="Self Evaluation"
                    icon={USER}
                    url={`/performance/review/${quadId}/self-evaluation`}
                  />
                  {hasGivenFeedbacks && (
                    <NavItemWithBadge
                      title="Given Feedbacks"
                      icon={MESSAGE}
                      url={`/performance/review/${quadId}/given-feedbacks`}
                      collection="performance_review"
                      filters={{
                        quadrimester: { _eq: quadId },
                        reviewer: { user: { _eq: "$CURRENT_USER" } },
                        reviewee: { user: { _neq: "$CURRENT_USER" } },
                        status: { _eq: "pending" },
                      }}
                    />
                  )}
                  <NavItem
                    title="Team Performance"
                    icon={USERS}
                    url={`/performance/review/${quadId}/team-performance`}
                  />
                  <NavItem
                    title="Leaderboard"
                    icon={AWARD}
                    url={`/performance/review/${quadId}/leaderboard`}
                  />
                  <NavItem
                    title="Pending Reviews"
                    icon={PENDING_REVIEW}
                    url={`/performance/review/${quadId}/pending-reviews`}
                    requiredPermissions={{
                      type: "collection",
                      collection: "performance_review_months",
                      action: "create",
                    }}
                  />
                  <NavItem
                    title="Pending Self Reviews"
                    icon={PENDING_SELF_REVIEW}
                    url={`/performance/review/${quadId}/pending-self-reviews`}
                    requiredPermissions={{
                      type: "collection",
                      collection: "performance_review_months",
                      action: "create",
                    }}
                  />
                  <NavItem
                    title="Reviewer Coverage"
                    icon={REVIEWER_LOAD}
                    url={`/performance/review/${quadId}/reviewer-coverage`}
                    requiredPermissions={{
                      type: "collection",
                      collection: "performance_review_months",
                      action: "create",
                    }}
                  />
                  <NavItem
                    title="Reviewee Coverage"
                    icon={REVIEW_COVERAGE}
                    url={`/performance/review/${quadId}/reviewee-coverage`}
                    requiredPermissions={{
                      type: "collection",
                      collection: "performance_review_months",
                      action: "create",
                    }}
                  />
                </SidebarMenu>
              </SidebarGroup>
            </SidebarContent>
            <SidebarRail />
          </Sidebar>
        </SidebarProvider>
        <div className="flex-1">
          <Outlet />
        </div>
      </div>
    </QuadProvider>
  );
};

export default PerformanceLayout;
